import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { CreateJobInput, OrgImportRowsInput, UpdateJobInput } from '@/lib/types';

export const jobKeys = {
  all: ['jobs'] as const,
  list: (includeArchived: boolean) => ['jobs', 'list', includeArchived] as const,
  detail: (jobId: string) => ['jobs', 'detail', jobId] as const,
  availableNumbers: ['phone-numbers', 'available'] as const,
  summary: (jobId: string) => ['jobs', jobId, 'reports', 'summary'] as const,
  daily: (jobId: string) => ['jobs', jobId, 'reports', 'daily'] as const,
};

export function useJobs(includeArchived = false) {
  return useQuery({ queryKey: jobKeys.list(includeArchived), queryFn: () => api.jobs.list(includeArchived) });
}

export function useJob(jobId: string) {
  return useQuery({ queryKey: jobKeys.detail(jobId), queryFn: () => api.jobs.get(jobId), enabled: !!jobId });
}

export function useAvailableNumbers(enabled: boolean) {
  return useQuery({ queryKey: jobKeys.availableNumbers, queryFn: () => api.jobs.availableNumbers(), enabled });
}

export function useJobSummary(jobId: string) {
  return useQuery({ queryKey: jobKeys.summary(jobId), queryFn: () => api.reports.summary(jobId), enabled: !!jobId });
}

export function useJobDaily(jobId: string) {
  return useQuery({ queryKey: jobKeys.daily(jobId), queryFn: () => api.reports.daily(jobId), enabled: !!jobId });
}

/** Daily series for the last `days` days (zero-filled), e.g. 60 to compare the last 30 with the 30 before. */
export function useJobDailyRange(jobId: string, days: number) {
  return useQuery({
    queryKey: [...jobKeys.daily(jobId), 'range', days],
    queryFn: () => {
      const to = new Date();
      const from = new Date(to.getTime() - days * 86_400_000);
      return api.reports.daily(jobId, { from: from.toISOString(), to: to.toISOString() });
    },
    enabled: !!jobId,
    staleTime: 60_000,
  });
}

export function useCreateJob() {
  const qc = useQueryClient();
  const { refreshMe } = useAuth();
  return useMutation({
    mutationFn: (body: CreateJobInput) => api.jobs.create(body),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: jobKeys.all }), qc.invalidateQueries({ queryKey: jobKeys.availableNumbers }), refreshMe()]);
    },
  });
}

export function useUpdateJob(jobId: string) {
  const qc = useQueryClient();
  const { refreshMe } = useAuth();
  return useMutation({
    mutationFn: (body: UpdateJobInput) => api.jobs.update(jobId, body),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: jobKeys.all }), qc.invalidateQueries({ queryKey: jobKeys.availableNumbers }), refreshMe()]);
    },
  });
}

/** Upload or remove the job's cover photo; refreshes every job view and the sidebar list. */
export function useJobCover(jobId: string) {
  const qc = useQueryClient();
  const { refreshMe } = useAuth();
  const done = async () => {
    await Promise.all([qc.invalidateQueries({ queryKey: jobKeys.all }), refreshMe()]);
  };
  const upload = useMutation({ mutationFn: (file: File) => api.jobs.uploadCover(jobId, file), onSuccess: done });
  const remove = useMutation({ mutationFn: () => api.jobs.removeCover(jobId), onSuccess: done });
  return { upload, remove };
}

/** Spreadsheet import of jobs (org admin). Invalidates every job query on success. */
export function useImportJobRows() {
  const qc = useQueryClient();
  const { refreshMe } = useAuth();
  return useMutation({
    mutationFn: (body: OrgImportRowsInput) => api.jobs.importRows(body),
    onSuccess: async () => {
      await Promise.all([qc.invalidateQueries({ queryKey: jobKeys.all }), refreshMe()]);
    },
  });
}
